/**
 * Q Signal Board - Design Tokens
 * Centralized theme constants for consistent styling
 *
 * PHASE 1: Updated with CSS variable references for institutional-grade UI
 *
 * @version 2.0.0
 * @see docs/q-ai/QAI_DASHBOARD.2.md
 */

// ═══════════════════════════════════════════════════════════════════════════
// COLORS - Aligned with tokens.css
// ═══════════════════════════════════════════════════════════════════════════

export const COLORS = {
  // Backgrounds (dark to light hierarchy) - STRICT from tokens.css
  bgBoard: '#0a0a0f',      // var(--bg-primary)
  bgCard: '#12121a',       // var(--bg-surface) - UPDATED for better contrast
  bgHover: '#14141a',      // var(--bg-hover)
  bgElevated: '#1a1a24',   // var(--bg-surface-2) - UPDATED
  bgSurface3: '#22222e',   // var(--bg-surface-3)

  // Text (WCAG AA+ compliant) - STRICT from tokens.css
  textBright: '#ffffff',   // var(--text-bright)
  textPrimary: '#f0f0f5',  // var(--text-primary) - UPDATED
  textBody: '#e4e4e7',     // Body text
  textSecondary: '#9ca3af', // var(--text-secondary) - UPDATED
  textMuted: '#6b7280',    // var(--text-muted) - UPDATED
  textDim: '#4b5563',      // var(--text-disabled)

  // Signal Colors - STRICT
  signalBuy: '#00c853',    // var(--color-bullish) - UPDATED for institutional green
  signalSell: '#ff1744',   // var(--color-bearish) - UPDATED for institutional red
  signalNeutral: '#9ca3af', // Neutral gray
  signalWarning: '#f59e0b', // var(--color-warning)
  signalInfo: '#3b82f6',   // var(--color-info)

  // Signal Backgrounds (8% opacity)
  signalBuyBg: 'rgba(0, 200, 83, 0.08)',    // Based on --color-bullish
  signalSellBg: 'rgba(255, 23, 68, 0.08)',  // Based on --color-bearish
  signalNeutralBg: 'rgba(156, 163, 175, 0.06)',
  signalWarningBg: 'rgba(245, 158, 11, 0.08)',
  signalInfoBg: 'rgba(59, 130, 246, 0.08)',

  // Signal Borders (20% opacity)
  signalBuyBorder: 'rgba(0, 200, 83, 0.20)',
  signalSellBorder: 'rgba(255, 23, 68, 0.20)',
  signalNeutralBorder: 'rgba(156, 163, 175, 0.15)',
  signalWarningBorder: 'rgba(245, 158, 11, 0.20)',
  signalInfoBorder: 'rgba(59, 130, 246, 0.20)',

  // Borders - STRICT from tokens.css
  borderDefault: '#2a2a3a',  // var(--border-default) - UPDATED to solid color
  borderSubtle: '#1f1f2e',   // var(--border-subtle)
  borderHover: 'rgba(255, 255, 255, 0.10)',
  borderActive: 'rgba(255, 255, 255, 0.14)',

  // Brand
  brandCyan: '#00d4ff',    // var(--brand-cyan)
  brandGreen: '#27b56d',   // var(--brand-green)
  brandGradient: 'linear-gradient(135deg, #27b56d 0%, #22c55e 100%)',

  // Card Icon Colors
  iconZScore: '#6366f1',
  iconTrend: '#00c853',
  iconPercentile: '#3b82f6',
  iconCorrelation: '#8b5cf6',
  iconPnL: '#f59e0b',
};

// ═══════════════════════════════════════════════════════════════════════════
// TYPOGRAPHY - Aligned with tokens.css (PHASE 1)
// STRICT: No font below 10px, use consistent scale
// ═══════════════════════════════════════════════════════════════════════════

export const FONTS = {
  primary: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",  // var(--font-sans)
  mono: "'JetBrains Mono', 'Fira Code', 'SF Mono', monospace",        // var(--font-mono)
};

export const TYPOGRAPHY = {
  // Big numbers (Z-Score, main metrics) - 28px STRICT
  bigNumber: {
    fontSize: '28px',       // var(--text-2xl) - EXPLICIT px for consistency
    fontWeight: 700,
    fontFamily: FONTS.mono,
    lineHeight: 1.1,
    letterSpacing: '-0.02em',
  },

  // P&L values - 20px
  pnlValue: {
    fontSize: '20px',       // var(--text-xl)
    fontWeight: 700,
    fontFamily: FONTS.mono,
    lineHeight: 1.2,
    letterSpacing: '-0.01em',
  },

  // Signal scores - 16px
  score: {
    fontSize: '16px',       // var(--text-lg)
    fontWeight: 700,
    fontFamily: FONTS.mono,
    lineHeight: 1.2,
  },

  // Price levels - 14px
  price: {
    fontSize: '14px',       // var(--text-base)
    fontWeight: 600,
    fontFamily: FONTS.mono,
    lineHeight: 1.3,
  },

  // Card/Section titles (uppercase) - 14px
  title: {
    fontSize: '14px',       // var(--text-base) - UPDATED from 13px
    fontWeight: 600,
    fontFamily: FONTS.primary,
    lineHeight: 1.3,
    letterSpacing: '0.05em', // UPDATED for better tracking
    textTransform: 'uppercase',
  },

  // Body text - 13px
  body: {
    fontSize: '13px',       // var(--text-sm)
    fontWeight: 400,
    fontFamily: FONTS.primary,
    lineHeight: 1.4,
  },

  // Table headers / Section labels (uppercase, muted) - 11px STRICT
  sectionLabel: {
    fontSize: '11px',       // var(--text-xs) - Minimum readable header size
    fontWeight: 600,
    fontFamily: FONTS.primary,
    lineHeight: 1.3,
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
  },

  // Table data - 13px
  tableData: {
    fontSize: '13px',       // var(--text-sm)
    fontWeight: 400,
    fontFamily: FONTS.mono,
    lineHeight: 1.3,
  },

  // Captions - 11px
  caption: {
    fontSize: '11px',       // var(--text-xs)
    fontWeight: 500,
    fontFamily: FONTS.primary,
    lineHeight: 1.3,
    letterSpacing: '0.02em',
  },

  // Micro (footer only, special use) - 10px MINIMUM
  micro: {
    fontSize: '10px',       // var(--text-2xs) - STRICT MINIMUM
    fontWeight: 500,
    fontFamily: FONTS.primary,
    lineHeight: 1.2,
    letterSpacing: '0.03em',
  },

  // Chip labels - 11px
  chip: {
    fontSize: '11px',       // var(--text-xs)
    fontWeight: 600,
    fontFamily: FONTS.primary,
    lineHeight: 1,
  },

  // Symbol display - 13px
  symbol: {
    fontSize: '13px',       // var(--text-sm)
    fontWeight: 600,
    fontFamily: FONTS.primary,
    lineHeight: 1.3,
  },
};

// ═══════════════════════════════════════════════════════════════════════════
// SPACING - COMPACT (8px base grid) - Aligned with tokens.css (PHASE 1)
// STRICT: Use these exact values, no Tailwind defaults
// ═══════════════════════════════════════════════════════════════════════════

export const SPACING = {
  xs: '4px',   // var(--space-1) Icon-to-text, tight gaps
  sm: '8px',   // var(--space-3) PRIMARY gap, card gaps
  md: '12px',  // var(--space-4) Card padding, content padding
  lg: '16px',  // var(--space-5) Section gaps (USE SPARINGLY)
  xl: '20px',  // Large gaps (RARE)
  xxl: '24px', // var(--space-6) Major section margins (RARE)
};

// ═══════════════════════════════════════════════════════════════════════════
// BORDER RADIUS - Aligned with tokens.css
// ═══════════════════════════════════════════════════════════════════════════

export const RADIUS = {
  xs: '4px',   // var(--radius-xs) Chips, badges
  sm: '6px',   // var(--radius-sm) Buttons, stat cells
  md: '8px',   // var(--radius-md) Cards
  lg: '10px',  // var(--radius-lg) Board container
  xl: '12px',  // var(--radius-xl) Large containers
};

// ═══════════════════════════════════════════════════════════════════════════
// HEIGHTS - STRICT (fixed dimensions) - Aligned with tokens.css (PHASE 1)
// These are MANDATORY heights for institutional density
// ═══════════════════════════════════════════════════════════════════════════

export const HEIGHTS = {
  header: 48,       // var(--h-header) Nav bar / Board header - REDUCED from original
  sectionTitle: 36, // var(--h-section-title) Section header row
  rowCompact: 32,   // var(--h-row-compact) Compact table row - STRICT
  rowNormal: 36,    // var(--h-row-normal) Normal table row
  rowData: 40,      // var(--h-row-data) Data-heavy row
  footer: 40,       // var(--h-footer) Board footer
  pnlCard: 56,      // P&L summary card (REDUCED)
  pnlLabel: 12,     // P&L section label
  signalCard: 44,   // Collapsed signal card (REDUCED from 48)
  tfBox: 52,        // Timeframe box height (REDUCED)
  statCell: 44,     // Stat cell height (REDUCED from 48)
  actionButton: 32, // var(--h-action-btn) Action button height
  iconBox: 24,      // Icon container (REDUCED from 28)
  chip: 24,         // var(--h-chip) Signal chip height
};

// ═══════════════════════════════════════════════════════════════════════════
// SHADOWS
// ═══════════════════════════════════════════════════════════════════════════

export const SHADOWS = {
  card: '0 1px 2px rgba(0, 0, 0, 0.2)',
  cardHover: '0 4px 12px rgba(0, 0, 0, 0.3)',
  glow: '0 0 20px rgba(39, 181, 109, 0.25)',
  iconGlow: '0 2px 8px rgba(39, 181, 109, 0.25)',
};

// ═══════════════════════════════════════════════════════════════════════════
// ANIMATIONS
// ═══════════════════════════════════════════════════════════════════════════

export const TRANSITIONS = {
  fast: '150ms ease',
  normal: '200ms ease-out',
  slow: '300ms ease',
};

export const ANIMATIONS = {
  pulse: {
    '@keyframes pulse': {
      '0%, 100%': {
        opacity: 1,
        boxShadow: '0 0 0 0 rgba(39, 181, 109, 0.4)'
      },
      '50%': {
        opacity: 0.8,
        boxShadow: '0 0 0 4px rgba(39, 181, 109, 0)'
      },
    },
    animation: 'pulse 2s infinite',
  },
  skeleton: {
    '@keyframes skeleton': {
      '0%': { opacity: 0.4 },
      '50%': { opacity: 0.7 },
      '100%': { opacity: 0.4 },
    },
    animation: 'skeleton 1.5s ease-in-out infinite',
  },
};

// ═══════════════════════════════════════════════════════════════════════════
// HELPER FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Get signal color based on type
 * @param {'buy' | 'sell' | 'neutral' | 'warning' | 'info'} type
 * @returns {string} Color hex
 */
export const getSignalColor = (type) => {
  switch (type) {
    case 'buy': return COLORS.signalBuy;
    case 'sell': return COLORS.signalSell;
    case 'warning': return COLORS.signalWarning;
    case 'info': return COLORS.signalInfo;
    default: return COLORS.signalNeutral;
  }
};

/**
 * Get signal background based on type
 * @param {'buy' | 'sell' | 'neutral' | 'warning' | 'info'} type
 * @returns {string} RGBA background
 */
export const getSignalBg = (type) => {
  switch (type) {
    case 'buy': return COLORS.signalBuyBg;
    case 'sell': return COLORS.signalSellBg;
    case 'warning': return COLORS.signalWarningBg;
    case 'info': return COLORS.signalInfoBg;
    default: return COLORS.signalNeutralBg;
  }
};

/**
 * Get signal border based on type
 * @param {'buy' | 'sell' | 'neutral' | 'warning' | 'info'} type
 * @returns {string} RGBA border
 */
export const getSignalBorder = (type) => {
  switch (type) {
    case 'buy': return COLORS.signalBuyBorder;
    case 'sell': return COLORS.signalSellBorder;
    case 'warning': return COLORS.signalWarningBorder;
    case 'info': return COLORS.signalInfoBorder;
    default: return COLORS.signalNeutralBorder;
  }
};

/**
 * Format P&L value with sign and color
 * @param {number | null} value
 * @returns {{ text: string, color: string }}
 */
export const formatPnL = (value) => {
  if (value === null || value === undefined) {
    return { text: '--', color: COLORS.textDim };
  }
  const sign = value >= 0 ? '+' : '';
  const color = value >= 0 ? COLORS.signalBuy : COLORS.signalSell;
  return {
    text: `${sign}$${Math.abs(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
    color
  };
};

/**
 * Get trend direction arrow and color
 * @param {'bull' | 'bear' | 'neutral'} direction
 * @returns {{ arrow: string, color: string }}
 */
export const getTrendIndicator = (direction) => {
  switch (direction) {
    case 'bull': return { arrow: '▲', color: COLORS.signalBuy };
    case 'bear': return { arrow: '▼', color: COLORS.signalSell };
    default: return { arrow: '—', color: COLORS.signalNeutral };
  }
};

// ═══════════════════════════════════════════════════════════════════════════
// SHARED SX STYLES
// ═══════════════════════════════════════════════════════════════════════════

export const SHARED_STYLES = {
  // Scrollbar styling
  scrollbar: {
    '&::-webkit-scrollbar': { width: '4px' },
    '&::-webkit-scrollbar-track': { background: 'transparent' },
    '&::-webkit-scrollbar-thumb': {
      background: 'rgba(255, 255, 255, 0.08)',
      borderRadius: '2px'
    },
  },

  // Card base style
  cardBase: {
    backgroundColor: COLORS.bgCard,
    border: `1px solid ${COLORS.borderDefault}`,
    borderRadius: RADIUS.md,
    transition: TRANSITIONS.fast,
    '&:hover': {
      backgroundColor: COLORS.bgHover,
      borderColor: COLORS.borderHover,
    },
  },

  // Section label style
  sectionLabel: {
    ...TYPOGRAPHY.sectionLabel,
    color: COLORS.textMuted,
    marginBottom: SPACING.sm,
  },

  // Stat cell style
  statCell: {
    backgroundColor: COLORS.bgElevated,
    borderRadius: RADIUS.sm,
    padding: `${SPACING.sm} ${SPACING.md}`,
  },
};

export default {
  COLORS,
  FONTS,
  TYPOGRAPHY,
  SPACING,
  RADIUS,
  HEIGHTS,
  SHADOWS,
  TRANSITIONS,
  ANIMATIONS,
  SHARED_STYLES,
  getSignalColor,
  getSignalBg,
  getSignalBorder,
  formatPnL,
  getTrendIndicator,
};
